import { Source, Layer } from 'react-map-gl';
import { colours } from './colours';

const countOutlets = (outlets, boundaries, statsData, idKey, coverageKey) => {
    if (!boundaries || !boundaries.features) return new Map();
    const statsMap = new Map();
    const statsDataMap = new Map((statsData || []).map(s => [s.id, s]));

    boundaries.features.forEach(feature => {
        const id = feature.properties[idKey];
        const outletsInBoundary = outlets.filter(outlet => outlet[coverageKey]?.includes(id));
        const stat = statsDataMap.get(id);

        const total = outletsInBoundary.length;
        const per100k = stat ? (total / (stat.pop / 100000)) : 0;

        statsMap.set(id, { total, per100k });
    });

    return statsMap;
}

const Boundaries = ({
    activeConstituency,
    selectedOutlet,
    searchMode,
    outlets,
    mapMetric = 'total',
    boundaryType = 'constituency',
    ladStats = [],
    boundaries,
    statsData,
    dynamicScale
}) => {
    if (!boundaries || !boundaries.features) return null;
    let highlightConstituency;

    // Zoom/Centering for boundaries (ensure bounding boxes exist - now handled in App.js)
    const processedBoundaries = boundaries;

    const idKey = boundaryType === 'constituency' ? 'PCON24CD' : 'LAD24CD';
    const coverageKey = boundaryType === 'constituency' ? 'COVERAGE_CONSTITUENCY' : 'COVERAGE_LAD';
    const currentStats = boundaryType === 'constituency' ? statsData : ladStats;

    const statsByBoundary = countOutlets(outlets, processedBoundaries, currentStats, idKey, coverageKey);

    processedBoundaries.features.forEach(feature => {
        const id = feature.properties[idKey];
        if (activeConstituency && (activeConstituency[idKey] === id))
            highlightConstituency = feature;

        const stat = statsByBoundary.get(id);
        feature.properties.outlets = stat ? stat.total : 0;
        feature.properties.per100k = stat ? stat.per100k : 0;
    });

    const isPer100k = mapMetric === 'per100k';
    const scale = isPer100k ? (dynamicScale || colours.SCALE_100K) : colours.SCALE;

    // Build step expression dynamically from scale to handle varying number of bins
    const buildStepExpression = () => {
        if (searchMode === 'outlet') return colours.WHITE;
        if (!scale || scale.length < 2) return colours.WHITE;

        const stepExpr = [
            'step',
            ['get', isPer100k ? 'per100k' : 'outlets'],
            scale[0].color
        ];

        for (let i = 1; i < scale.length; i++) {
            stepExpr.push(scale[i].min, scale[i].color);
        }

        return stepExpr;
    };

    const dataLayer = {
        id: 'data',
        type: 'fill',
        paint: {
            'fill-color': buildStepExpression(),
            'fill-opacity': 0.5,
            "fill-outline-color": '#265089'
        }
    }

    const highlightConstituencyStyle = {
        type: "line",
        paint: {
            "line-color": '#265089',
            "line-width": 4
        }
    };

    const highlightConstituencyFeature = {
        "type": "FeatureCollection",
        "features": [highlightConstituency]
    }

    // New layer for outlet coverage - thick border
    const outletCoverageLayer = {
        id: 'outlet-coverage',
        type: 'line',
        paint: {
            'line-color': '#265089',
            'line-width': 5
        }
    };

    const coverageFeatures = searchMode === 'outlet' && selectedOutlet ? {
        "type": "FeatureCollection",
        "features": processedBoundaries.features.filter(f => selectedOutlet[coverageKey]?.includes(f.properties[idKey]))
    } : null;

    // Source component only does a shallow compare to see if the data is different
    // so we change the name randomly and create a new object
    processedBoundaries.name = `${boundaryType}-${Math.random()}`;
    const boundariesToRender = Object.assign({}, processedBoundaries);

    return <>
        <Source type="geojson" data={boundariesToRender}>
            <Layer {...dataLayer} />
        </Source>

        {coverageFeatures && <Source type="geojson" data={coverageFeatures}>
            <Layer {...outletCoverageLayer} />
        </Source>}

        {highlightConstituency && <Source type="geojson" data={highlightConstituencyFeature}>
            <Layer {...highlightConstituencyStyle} />
        </Source>}
    </>
}

export default Boundaries;